import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", role: "", contact: "", email: "", notes: "" };

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [buyerCounts, setBuyerCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const load = async () => {
    const [{ data: e }, { data: b }] = await Promise.all([
      api.get("/employees"),
      api.get("/buyers"),
    ]);
    setRows(e);
    const counts = {};
    b.forEach((buyer) => {
      counts[buyer.employee_id] = (counts[buyer.employee_id] || 0) + 1;
    });
    setBuyerCounts(counts);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/employees/${editing.id}`, form);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", form);
        toast.success("Employee added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Move ${r.name} to recycle bin?`)) return;
    await api.delete(`/employees/${r.id}`);
    toast.success("Moved to recycle bin");
    load();
  };

  const filtered = rows.filter((r) =>
    (r.name + " " + (r.role || "")).toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8 animate-fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-ink-muted">
            Team
          </div>
          <h1 className="font-display text-5xl font-semibold tracking-tight mt-1">
            Employees
          </h1>
          <p className="text-ink-muted mt-2">
            Add your team members. Each buyer gets assigned to one of them.
          </p>
        </div>
        <Button
          className="btn-primary py-5"
          onClick={() => {
            setEditing(null);
            setForm(empty);
            setOpen(true);
          }}
          data-testid="employees-new-btn"
        >
          <Plus size={16} strokeWidth={1.75} /> New Employee
        </Button>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search…"
        className="hygenix-input max-w-xs mb-6"
        data-testid="employees-search"
      />

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone-50 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Name
              </th>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Role
              </th>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Contact
              </th>
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Email
              </th>
              <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-ink-muted">
                Parties
              </th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center p-10 text-ink-muted">
                  No employees yet — add your team to get started.
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr
                key={r.id}
                className="border-b border-neutral-100 last:border-0 hover:bg-bone-50 transition-colors"
                data-testid={`emp-row-${r.name}`}
              >
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-ink-muted">{r.role}</td>
                <td className="px-4 py-3 text-ink-muted">{r.contact}</td>
                <td className="px-4 py-3 text-ink-muted">{r.email}</td>
                <td className="px-4 py-3 text-right font-mono-num">
                  {buyerCounts[r.id] || 0}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => {
                        setEditing(r);
                        setForm({ ...empty, ...r });
                        setOpen(true);
                      }}
                      className="p-2 rounded-lg hover:bg-bone-100 text-ink-soft"
                      data-testid={`emp-edit-${r.name}`}
                    >
                      <Pencil size={14} strokeWidth={1.75} />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="p-2 rounded-lg hover:bg-terracotta-100 text-terracotta-600"
                      data-testid={`emp-delete-${r.name}`}
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit Employee" : "New Employee"}
            </DialogTitle>
            <DialogDescription>
              Employees are the team members through whom orders come in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="hygenix-input"
                data-testid="emp-name"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="hygenix-input"
                data-testid="emp-role"
              />
            </div>
            <div>
              <Label>Contact</Label>
              <Input
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                className="hygenix-input"
                data-testid="emp-contact"
              />
            </div>
            <div className="col-span-2">
              <Label>Email</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="hygenix-input"
                data-testid="emp-email"
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="hygenix-input"
              />
            </div>
            <div className="col-span-2 pt-2">
              <Button
                type="submit"
                className="w-full btn-primary py-5"
                data-testid="emp-submit"
              >
                {editing ? "Save changes" : "Create employee"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
